// Prod form

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { countryList } from "../utils/utils";
import { getDemoFlowsByGroupLink, makeCallProd } from "../services/flowService";
import PhoneIcon from "../assets/PhoneIcon";
import {
  getProdServicesStatus,
  getProdServicesStatusFromTeleserver,
  startProdServices,
  teleserverUrlFromStatus,
} from "../services/configService";
import RefreshIcon from "../assets/RefreshIcon";
import LoadingSpinner from "../assets/LoadingSpinner";
import {
  getAutoStopWarning,
  isProdServicesUp,
  shouldPollProdServicesStatus,
  STATUS_POLL_INTERVAL_MS,
  STATUS_POLL_WINDOW_MS,
} from "../utils/prodServices";

const getVariableDefaultFormValue = (variable) => {
  if (variable?.type === "list") {
    if (Array.isArray(variable?.default_value)) {
      return JSON.stringify(variable.default_value);
    }
    return "[]";
  }
  return variable?.default_value || "";
};

const getVariableDefaultPayloadValue = (variable) => {
  if (variable?.type === "list") {
    if (Array.isArray(variable?.default_value)) {
      return variable.default_value;
    }
    if (typeof variable?.default_value === "string") {
      try {
        const parsed = JSON.parse(variable.default_value);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    }
    return [];
  }

  return variable?.default_value || "";
};

const evaluateShowIfCondition = (condition, values) => {
  const leftValue = values?.[condition?.variable];
  const rightValue = condition?.value;
  const operator = condition?.operator || "equals";

  if (operator === "equals") {
    return String(leftValue ?? "") === String(rightValue ?? "");
  }

  if (operator === "not_equals") {
    return String(leftValue ?? "") !== String(rightValue ?? "");
  }

  if (operator === "contains") {
    return String(leftValue ?? "").includes(String(rightValue ?? ""));
  }

  if (operator === "in") {
    return Array.isArray(rightValue) ? rightValue.includes(leftValue) : false;
  }

  return false;
};

const shouldShowVariable = (variable, values) => {
  const showIf = variable?.show_if;

  if (!showIf || !Array.isArray(showIf?.conditions) || showIf.conditions.length === 0) {
    return true;
  }

  const results = showIf.conditions.map((condition) =>
    evaluateShowIfCondition(condition, values)
  );

  if (showIf.match_type === "any") {
    return results.some(Boolean);
  }

  return results.every(Boolean);
};

const getFilteredOptions = (variable, values) => {
  const filterOptions = variable?.filter_options;
  if (!filterOptions || !filterOptions.variable || !filterOptions.mappings) {
    return variable?.options || [];
  }
  const parentValue = values?.[filterOptions.variable];
  if (!parentValue) {
    return variable?.options || [];
  }
  const trimmedParent = parentValue.trim();
  // Try exact match first, then trimmed key match to handle leading/trailing spaces
  if (filterOptions.mappings[parentValue]) {
    return filterOptions.mappings[parentValue];
  }
  const matchedKey = Object.keys(filterOptions.mappings).find(
    (key) => key.trim() === trimmedParent
  );
  if (matchedKey) {
    return filterOptions.mappings[matchedKey];
  }
  return variable?.options || [];
};

const MakeCallForm = ({ company_id, selectedFlowId, setSelectedFlowId }) => {
  const [loading, setLoading] = useState();
  const [msg, setMsg] = useState("");
  const [autoStopWarning, setAutoStopWarning] = useState("");
  const [flows, setFlows] = useState([]);

  useEffect(() => {
    const getAllFlowsByGroupId = async (group_link) => {
      const response = await getDemoFlowsByGroupLink(group_link);
      setFlows(response);
    };

    getAllFlowsByGroupId(company_id);
  }, [company_id]);

  //Dynamic form values

  let selectedFlow = "";

  if (Array.isArray(flows)) {
    selectedFlow = flows?.find((flow) => flow.id === selectedFlowId);
  }

  const filteredVariablesArr = useMemo(() => {
    return (
      selectedFlow?.variables?.variables.filter((elem) => elem.value_required) ||
      []
    );
  }, [selectedFlow]);

  const sortedVariablesArr = useMemo(() => {
    return [...filteredVariablesArr].sort((a, b) => {
      const aHasDependency = !!a?.show_if;
      const bHasDependency = !!b?.show_if;
      if (aHasDependency === bHasDependency) return 0;
      return aHasDependency ? 1 : -1;
    });
  }, [filteredVariablesArr]);

  const variableByName = useMemo(() => {
    return sortedVariablesArr.reduce((acc, variable) => {
      acc[variable.name] = variable;
      return acc;
    }, {});
  }, [sortedVariablesArr]);

  const form_variable_fields = useMemo(() => {
    return sortedVariablesArr?.reduce((obj, elem) => {
      if (elem.type === "phone") {
        return {
          ...obj,
          [elem.name]: getVariableDefaultFormValue(elem),
          [`countryCode_${elem.name}`]: "+91",
        };
      } else if (elem.type === "list") {
        return {
          ...obj,
          [elem.name]: getVariableDefaultFormValue(elem),
        };
      } else {
        return {
          ...obj,
          [elem.name]: getVariableDefaultFormValue(elem),
        };
      }
    }, {}) || {};
  }, [sortedVariablesArr]);

  // Yup validation schema

  const validationSchema = useMemo(() => {
    const isFieldVisible = (fieldName, currentValues) => {
      const isDynamicCountryCode = fieldName.startsWith("countryCode_");
      const dynamicVariableName = isDynamicCountryCode
        ? fieldName.replace("countryCode_", "")
        : fieldName;
      const variable = variableByName[dynamicVariableName];
      if (!variable) return true;
      return shouldShowVariable(variable, currentValues || {});
    };

    return Yup.object().shape(
      Object.keys(form_variable_fields).reduce(
        (obj, elem) => {
          const isDynamicCountryCode = elem.startsWith("countryCode_");
          const dynamicVariableName = isDynamicCountryCode
            ? elem.replace("countryCode_", "")
            : elem;
          const other_variable = variableByName[dynamicVariableName];
          const isOptional = !!other_variable?.is_optional;

          if (!elem) {
            return { ...obj };
          } else if (isDynamicCountryCode) {
            return {
              ...obj,
              [elem]: Yup.string().test(
                "required-if-visible",
                `${elem} is required`,
                function (value) {
                  if (!isFieldVisible(elem, this.parent)) return true;
                  if (isOptional) return true;
                  return !!value;
                }
              ),
            };
          } else if (other_variable?.type === "phone") {
            return {
              ...obj,
              [elem]: Yup.string()
                .test(
                  "phone-if-visible",
                  `${elem} must be 10 digit`,
                  function (value) {
                    if (!isFieldVisible(elem, this.parent)) return true;
                    if (isOptional && !value) return true;
                    return /^[0-9]{10}$/.test(value || "");
                  }
                )
                .test(
                  "required-if-visible",
                  `${elem} is required`,
                  function (value) {
                    if (!isFieldVisible(elem, this.parent)) return true;
                    if (isOptional) return true;
                    return !!value;
                  }
                ),
            };
          } else if (other_variable?.type === "list") {
            return {
              ...obj,
              [elem]: Yup.string()
                .test(
                  "required-if-visible",
                  `${elem} is required`,
                  function (value) {
                    if (!isFieldVisible(elem, this.parent)) return true;
                    if (isOptional) return true;
                    return !!value;
                  }
                )
                .test(
                  "is-valid-json-array-if-visible",
                  `${elem} must be a valid JSON array`,
                  function (value) {
                    if (!isFieldVisible(elem, this.parent)) return true;
                    if (isOptional && !value) return true;
                    try {
                      const parsed = JSON.parse(value);
                      return Array.isArray(parsed);
                    } catch (e) {
                      return false;
                    }
                  }
                ),
            };
          } else {
            return {
              ...obj,
              [elem]: Yup.string()
                .test(
                  "required-if-visible",
                  `${elem} is required`,
                  function (value) {
                    if (!isFieldVisible(elem, this.parent)) return true;
                    if (isOptional) return true;
                    return !!value;
                  }
                ),
            };
          }
        },
        {
          name: Yup.string()
            .min(1, "Name must be atleast 1 character")
            .matches(/^[^_]*$/, "Name cannot contain underscore")
            .required("Name is required"),
          countryCode: Yup.string().required("Country Code is required"),
          phone: Yup.string()
            .matches(/^[0-9]{10}$/, "Phone number must be 10 digit")
            .required("Phone number is required"),
        }
      )
    );
  }, [form_variable_fields, variableByName]);

  // Start Prod Services

  const [servicesStartTime, setServicesStartTime] = useState("");
  const [startProdServicesLoading, setStartProdServicesLoading] =
    useState(false);
  const [startProdServicesMessage, setStartProdServicesMessage] = useState("");
  // When to stop re-checking the status (see shouldPollProdServicesStatus).
  const [statusPoll, setStatusPoll] = useState({ until: 0, afterStart: false });

  const handleStartProdServicesClick = async () => {
    setStartProdServicesLoading(true);
    setStartProdServicesMessage("");
    let startFailed = false;
    try {
      const response = await startProdServices(selectedFlowId);
      if (response?.data?.status === "success") setAutoStopWarning("");
      // The API answers 200 with status "failed" when it rolled the start back.
      startFailed = response?.data?.status === "failed";
      if (startFailed) {
        setStartProdServicesMessage(
          "Couldn't start prod services. Please try again in a minute."
        );
      }
    } catch (err) {
      // Timed out or the connection dropped. The server often still finishes
      // the start, so keep checking the status.
      setStartProdServicesMessage(
        "Prod services are taking longer than usual to start. The status above updates automatically."
      );
    }
    if (!startFailed) {
      setStatusPoll({
        until: Date.now() + STATUS_POLL_WINDOW_MS,
        afterStart: true,
      });
    }
    localStorage.setItem(
      `${selectedFlowId}_start_time`,
      new Date().toLocaleTimeString()
    );
    setServicesStartTime(new Date().toLocaleTimeString());

    // Keep showing "Starting..." until the fresh status is in.
    await handleGetProdServicesStatus({ silent: true });
    setStartProdServicesLoading(false);
  };

  useEffect(() => {
    if (localStorage.getItem(`${selectedFlowId}_start_time`)) {
      setServicesStartTime(
        localStorage.getItem(`${selectedFlowId}_start_time`)
      );
    }
  }, [selectedFlowId]);

  // prod services status
  const [prodServicesStatus, setProdServicesStatus] = useState({});
  const [prodServicesStatusLoading, setProdServicesStatusLoading] =
    useState(false);

  // silent: background re-check that keeps the last status on screen
  // instead of flashing "Loading...".
  const handleGetProdServicesStatus = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setProdServicesStatusLoading(true);
      try {
        const res = await getProdServicesStatus(selectedFlowId);
        // The teleserver refuses connections while prod pods are still
        // coming up. That only means "not up yet", so keep the schedule
        // status and treat the services as not up.
        const res2 = await getProdServicesStatusFromTeleserver(
          selectedFlowId,
          "",
          teleserverUrlFromStatus(res),
        ).catch(() => null);
        if (res?.data?.status === "success")
          setProdServicesStatus({
            ...res?.data?.message,
            services_status: res2?.data?.status,
            res2_message: res2?.data?.message,
            fetched_at: Date.now(),
          });
      } catch (err) {
        // getProdServicesStatus already logged it. Keep the last status.
      } finally {
        if (!silent) setProdServicesStatusLoading(false);
      }
    },
    [selectedFlowId]
  );

  useEffect(() => {
    if (selectedFlowId) {
      setStartProdServicesMessage("");
      setStatusPoll({
        until: Date.now() + STATUS_POLL_WINDOW_MS,
        afterStart: false,
      });
      handleGetProdServicesStatus();
    }
  }, [selectedFlowId, handleGetProdServicesStatus]);

  // Re-check while the services are starting, so the panel turns Up without
  // a manual refresh. The next check is scheduled only after the previous
  // one has landed.
  useEffect(() => {
    if (startProdServicesLoading || prodServicesStatusLoading) return;
    if (!shouldPollProdServicesStatus(prodServicesStatus, statusPoll)) return;
    const timer = setTimeout(
      () => handleGetProdServicesStatus({ silent: true }),
      STATUS_POLL_INTERVAL_MS
    );
    return () => clearTimeout(timer);
  }, [
    prodServicesStatus,
    prodServicesStatusLoading,
    startProdServicesLoading,
    statusPoll,
    handleGetProdServicesStatus,
  ]);

  return (
    <>
      <div className="my-4 text-base list-none text-black bg-white divide-y divide-gray-100 rounded-md shadow-lg md:w-[30rem] text-left p-4 border border-slate-300">
        <div className="space-y-4">
          <div>
            <label
              htmlFor="flow"
              className="block text-sm font-medium text-gray-700 break-words">
              <span className="text-red-500">*</span>Conversation Flow
            </label>
            <select
              name="flow"
              id="flow"
              onChange={(e) => {
                setSelectedFlowId(e.target.value);
              }}
              value={selectedFlowId}
              className={
                "mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md cursor-pointer"
              }>
              <option value="" disabled>
                Please select flow
              </option>
              {Array.isArray(flows) ? (
                flows.map((flow, index) => (
                  <option key={flow.id} value={flow.id}>
                    {flow.name}
                  </option>
                ))
              ) : (
                <span>No flows available for this url</span>
              )}
            </select>
          </div>

          <Formik
            initialValues={{
              name: "Kanik",
              countryCode: "+91",
              phone: "",
              ...form_variable_fields,
            }}
            enableReinitialize
            validationSchema={validationSchema}
            onSubmit={(values) => {
              setMsg("");
              const warning = getAutoStopWarning(
                prodServicesStatus,
                prodServicesStatusLoading
              );
              setAutoStopWarning(warning);
              // Same refresh as the status panel's button. Keep it after
              // getAutoStopWarning, which never blocks while a refresh loads.
              handleGetProdServicesStatus();
              if (warning) return;

              setLoading(true);

              // Calling Prod backend

              const data = sortedVariablesArr.reduce(
                (obj, variable) => {
                  const isVisible = shouldShowVariable(variable, values);

                  if (variable.type === "phone") {
                    const numberValue = isVisible
                      ? values[variable.name]
                      : getVariableDefaultFormValue(variable);
                    const countryCodeValue = isVisible
                      ? values[`countryCode_${variable.name}`]
                      : "+91";

                    return {
                      ...obj,
                      other_values: {
                        ...obj.other_values,
                        [variable.name]: `${countryCodeValue}${numberValue}`,
                      },
                    };
                  }

                  if (variable.type === "list") {
                    if (!isVisible) {
                      return {
                        ...obj,
                        other_values: {
                          ...obj.other_values,
                          [variable.name]: getVariableDefaultPayloadValue(variable),
                        },
                      };
                    }

                    try {
                      const parsedArray = JSON.parse(values[variable.name]);
                      return {
                        ...obj,
                        other_values: {
                          ...obj.other_values,
                          [variable.name]: parsedArray,
                        },
                      };
                    } catch (e) {
                      return {
                        ...obj,
                        other_values: {
                          ...obj.other_values,
                          [variable.name]: [],
                        },
                      };
                    }
                  }

                  return {
                    ...obj,
                    other_values: {
                      ...obj.other_values,
                      [variable.name]: isVisible
                        ? values[variable.name]
                        : getVariableDefaultPayloadValue(variable),
                    },
                  };
                },
                {
                  user_name: values.name,
                  user_phone: values.countryCode + values.phone,
                  other_values: {},
                }
              );

              makeCallProd(selectedFlowId, data)
                .then((response) => {
                  setLoading(false);
                  setMsg(response.data?.message);
                })
                .catch((err) => {
                  setLoading(false);
                  setMsg("some error occurred");
                });
            }}>
            {({ errors, touched, values, handleChange, handleBlur, setFieldValue }) => {
              return (
                <Form className="space-y-4">
                  {selectedFlowId && (
                    <div className="border border-gray-300 flex flex-col gap-2 p-2 rounded bg-gray-100">
                      <div className="flex gap-2 items-center">
                        <div className="text-nowrap border-r border-black pr-4">
                          <button
                            disabled={startProdServicesLoading}
                            onClick={handleStartProdServicesClick}
                            className="bg-gray-800 hover:bg-gray-700 disabled:bg-gray-300 text-gray-100 px-2 py-1 rounded cursor-pointer whitespace-nowrap">
                            {startProdServicesLoading
                              ? "Starting..."
                              : "Start Prod Services"}
                          </button>
                        </div>

                        <div className="flex flex-col pl-2 gap-1 w-full">
                          <div className="text-sm flex items-center gap-1 w-full">
                            <div>
                              Status:{" "}
                              {startProdServicesLoading &&
                              !isProdServicesUp(prodServicesStatus) ? (
                                <span>Starting...</span>
                              ) : prodServicesStatusLoading ? (
                                <span>Loading...</span>
                              ) : prodServicesStatus?.schedule_status ===
                                "Not Found" ? (
                                <span>Down</span>
                              ) : isProdServicesUp(prodServicesStatus) ? (
                                <span>Up</span>
                              ) : prodServicesStatus?.schedule_status ===
                                "Found" ? (
                                <span>Starting...</span>
                              ) : (
                                <span>Unknown</span>
                              )}
                            </div>

                            <div
                              disabled={prodServicesStatusLoading}
                              onClick={() => {
                                handleGetProdServicesStatus();
                              }}
                              className="ml-auto bg-gray-300 rounded hover:bg-gray-400 disabled:bg-gray-300 cursor-pointer flex items-center justify-center ver h-5 w-5">
                              {prodServicesStatusLoading ? (
                                "..."
                              ) : (
                                <RefreshIcon />
                              )}
                            </div>
                          </div>
                          {startProdServicesLoading ? (
                            <div className="mr-1 text-xs flex items-center gap-1">
                              <LoadingSpinner />
                              <span>
                                Starting prod services. This can take up to 2
                                minutes, please keep this page open.
                              </span>
                            </div>
                          ) : prodServicesStatusLoading ? (
                            <div className="h-[2.25rem]"></div>
                          ) : prodServicesStatus?.schedule_status ===
                            "Not Found" ? (
                            <>
                              <div className="mr-1 text-xs">
                                Services start in: NA
                              </div>
                              <div className="mr-1 text-xs">
                                Services stop in: NA
                              </div>
                            </>
                          ) : (
                            prodServicesStatus?.schedule_status === "Found" && (
                              <>
                                <div className="mr-1 text-xs">
                                  {prodServicesStatus?.services_status ? (
                                    <span>Services are up</span>
                                  ) : prodServicesStatus?.time_left_to_start_services !==
                                    "Services are up" ? (
                                    `Services start in: ${prodServicesStatus?.time_left_to_start_services} (approx)`
                                  ) : (
                                    `Taking longer than usual. Services will start in some time`
                                  )}
                                </div>
                                <div className="mr-1 text-xs">
                                  {`Services stop in: ${prodServicesStatus?.time_left_to_stop_prod}`}
                                </div>
                              </>
                            )
                          )}
                        </div>
                      </div>
                      <div className="text-sm">
                        {servicesStartTime
                          ? `After starting initially, please wait 15 minutes for services to start. Please click on the button again within 45 minutes post that, if you wish to continue using prod services.`
                          : ""}
                      </div>
                      {startProdServicesMessage &&
                        !isProdServicesUp(prodServicesStatus) && (
                          <div className="text-sm text-red-600">
                            {startProdServicesMessage}
                          </div>
                        )}
                    </div>
                  )}
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700 break-words">
                      <span className="text-red-500">*</span>Name
                    </label>
                    <Field
                      name="name"
                      type="text"
                      disabled={!!!selectedFlowId}
                      className={
                        "mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 disabled:border-gray-100 disabled:bg-gray-100 rounded-md disabled:text-slate-400 placeholder:text-slate-300" +
                        (errors.name && touched.name ? " border-red-500" : "")
                      }
                    />
                    <ErrorMessage
                      name="name"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>

                  <div className="flex w-full gap-3">
                    <div className="w-[50%]">
                      <label
                        htmlFor="countryCode"
                        className="block text-sm font-medium text-gray-700 break-words">
                        <span className="text-red-500">*</span>Phone
                      </label>
                      <Field
                        as="select"
                        name="countryCode"
                        disabled={!!!selectedFlowId}
                        className={
                          "mt-1 block w-full py-2 px-3 border border-gray-300 disabled:border-gray-100 disabled:bg-gray-100 bg-white rounded-md shadow-sm focus:outline-none  focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:text-slate-400" +
                          (errors.countryCode && touched.countryCode
                            ? " border-red-500"
                            : "")
                        }>
                        {countryList.map((country, index) => (
                          <option key={country.code} value={country.dial_code}>
                            {`${country.flag} (${country.dial_code})      ${country.code} ${country.name}`}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name="countryCode"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    <div className="w-full relative top-3 flex flex-col gap-2">
                      <label
                        htmlFor="phone"
                        className="block text-sm font-medium text-gray-700 break-words"></label>
                      <div className="gap-3">
                        <Field
                          name="phone"
                          type="tel"
                          disabled={!!!selectedFlowId}
                          className={
                            "mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 disabled:border-gray-100 disabled:bg-gray-100 rounded-md placeholder:text-slate-300" +
                            (errors.phone && touched.phone
                              ? " border-red-500"
                              : "")
                          }
                        />
                        <ErrorMessage
                          name="phone"
                          component="div"
                          className="text-red-500 text-sm mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  {sortedVariablesArr.map((v) => {
                    if (!shouldShowVariable(v, values)) {
                      return null;
                    }

                    if (v.type === "string") {
                      return (
                        <div key={v.name}>
                          <label
                            htmlFor={v.name}
                            className="block text-sm font-medium text-gray-700 break-words">
                            {!v.is_optional && (
                              <span className="text-red-500">*</span>
                            )}
                            {v.name}
                            {v.is_optional && (
                              <span className="text-xs text-gray-600">
                                {" (optional)"}
                              </span>
                            )}
                          </label>
                          <Field
                            name={v.name}
                            type="text"
                            className={
                              "mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" +
                              (errors[v.name] && touched[v.name]
                                ? " border-red-500"
                                : "")
                            }
                          />
                          <ErrorMessage
                            name={v.name}
                            component="div"
                            className="text-red-500 text-sm mt-1"
                          />
                        </div>
                      );
                    } else if (v.type === "dropdown") {
                      const filteredOpts = getFilteredOptions(v, values);
                      return (
                        <div key={v.name}>
                          <label
                            htmlFor={v.name}
                            className="block text-sm font-medium text-gray-700 break-words">
                            {!v.is_optional && (
                              <span className="text-red-500">*</span>
                            )}
                            {v.name}
                            {v.is_optional && (
                              <span className="text-xs text-gray-600">
                                {" (optional)"}
                              </span>
                            )}
                          </label>
                          <Field
                            className={
                              "mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" +
                              (errors[v.name] && touched[v.name]
                                ? " border-red-500"
                                : "")
                            }
                            as="select"
                            name={v.name}
                            onChange={(e) => {
                              setFieldValue(v.name, e.target.value);
                              // Reset dependent dropdowns when parent value changes
                              sortedVariablesArr.forEach((depVar) => {
                                if (
                                  depVar.filter_options?.variable === v.name &&
                                  depVar.type === "dropdown"
                                ) {
                                  const newFiltered = getFilteredOptions(
                                    depVar,
                                    { ...values, [v.name]: e.target.value }
                                  );
                                  if (
                                    values[depVar.name] &&
                                    !newFiltered.includes(values[depVar.name])
                                  ) {
                                    setFieldValue(depVar.name, "");
                                  }
                                }
                              });
                            }}>
                            <option value="">Select {v.name}</option>
                            {filteredOpts.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </Field>
                          <ErrorMessage
                            name={v.name}
                            component="div"
                            className="text-red-500 text-sm mt-1"
                          />
                        </div>
                      );
                    } else if (v.type === "phone") {
                      return (
                        <div key={v.name} className="flex w-full gap-3">
                          <div className="w-[50%]">
                            <label
                              htmlFor={`countryCode_${v.name}`}
                              className="block text-sm font-medium text-gray-700 break-words">
                              {!v.is_optional && (
                                <span className="text-red-500">*</span>
                              )}
                              {v.name}
                              {v.is_optional && (
                                <span className="text-xs text-gray-600">
                                  {" (optional)"}
                                </span>
                              )}
                            </label>
                            <Field
                              as="select"
                              name={`countryCode_${v.name}`}
                              className={
                                "mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" +
                                (errors[`countryCode_${v.name}`] &&
                                  touched[`countryCode_${v.name}`]
                                  ? " border-red-500"
                                  : "")
                              }>
                              {countryList.map((country, index) => (
                                <option
                                  key={country.code}
                                  value={country.dial_code}>
                                  {`${country.flag} (${country.dial_code})      ${country.code} ${country.name}`}
                                </option>
                              ))}
                            </Field>
                            <ErrorMessage
                              name={`countryCode_${v.name}`}
                              component="div"
                              className="text-red-500 text-sm mt-1"
                            />
                          </div>

                          <div className="w-full relative top-3 flex flex-col gap-2">
                            <label
                              htmlFor={v.name}
                              className="block text-sm font-medium text-gray-700 break-words"></label>
                            <div className="gap-3">
                              <Field
                                name={v.name}
                                type="number"
                                className={
                                  "mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" +
                                  (errors[v.name] && touched[v.name]
                                    ? " border-red-500"
                                    : "")
                                }
                              />
                              <ErrorMessage
                                name={v.name}
                                component="div"
                                className="text-red-500 text-sm mt-1"
                              />
                            </div>
                          </div>
                        </div>
                      );
                    } else if (v.type === "list") {
                      return (
                        <div key={v.name}>
                          <label
                            htmlFor={v.name}
                            className="block text-sm font-medium text-gray-700 break-words">
                            {!v.is_optional && (
                              <span className="text-red-500">*</span>
                            )}
                            {v.name} (JSON Array
                            {v.is_optional ? ", optional" : ""})
                          </label>
                          <Field
                            name={v.name}
                            as="textarea"
                            rows={4}
                            className={
                              "mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md font-mono" +
                              (errors[v.name] && touched[v.name]
                                ? " border-red-500"
                                : "")
                            }
                          />
                          <div className="text-xs text-gray-500 mt-1">
                            Enter a valid JSON array, e.g., ["item1", "item2",
                            "item3"] or [1,2,3]
                          </div>
                          <ErrorMessage
                            name={v.name}
                            component="div"
                            className="text-red-500 text-sm mt-1"
                          />
                        </div>
                      );
                    } else if (v.type === "textarea") {
                      return (
                        <div key={v.name}>
                          <label
                            htmlFor={v.name}
                            className="block text-sm font-medium text-gray-700 break-words">
                            {!v.is_optional && (
                              <span className="text-red-500">*</span>
                            )}
                            {v.name}
                            {v.is_optional && (
                              <span className="text-xs text-gray-600">
                                {" (optional)"}
                              </span>
                            )}
                          </label>
                          <Field
                            as="textarea"
                            name={v.name}
                            rows={4} // Set the desired number of rows
                            className={
                              "mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" +
                              (errors[v.name] && touched[v.name]
                                ? " border-red-500"
                                : "")
                            }
                          />
                          <ErrorMessage
                            name={v.name}
                            component="div"
                            className="text-red-500 text-sm mt-1"
                          />
                        </div>
                      );
                    }

                    return null;
                  })}

                  <div className="flex flex-col items-center gap-2 justify-center z-30">
                    <button
                      type="submit"
                      disabled={loading || !!!selectedFlowId}
                      className={`border-[1px] rounded text-white px-3 py-2 font-bold text-sm flex items-center gap-2 disabled:bg-gray-100 disabled:border-gray-100 disabled:text-slate-400 ${loading
                          ? "bg-gray-300 text-slate-400"
                          : "bg-sky-600 hover:bg-sky-700"
                        }`}>
                      <PhoneIcon />
                      <span>Start Call</span>
                    </button>
                    <div>{autoStopWarning || msg}</div>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
    </>
  );
};

export default MakeCallForm;
